import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import cn from 'classnames';

import SetupForm from 'components/billing/SetupForm.component';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import Space from 'components/generic/Space.component';
import SM from 'components/sidebar/appointment/wizard/Wizard.module.scss';
import CONF from 'config/conf.singleton';
import LINK from 'constants/link.const';
import SIGN_UP_METHOD from 'constants/signUpMethod.const';
import S from 'constants/string.const';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectCurrentSalonSignUpMethod } from 'selectors/currentSalon.selectors';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import call from 'utils/functions/call.util';
import linkTo from 'utils/links/linkTo.util';

import CN from './ClientCardForm.module.scss';

const stripePromise = loadStripe(CONF.stripeKey);

const LINK_TERMS = 'https://www.getslick.com/legal/terms';

// TODO: This flow needs refactoring + BillingLayout component
// We currently show the success modal before fetching updated billing/subscription data from the BE

const ClientCardForm = ({
  clientSecret,
  handleGoBack,
  isNew,
  onChange,
  salonId,
  subscriptionId,
  signUpMethod,
  isBillingIncomplete,
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const { val: isModalRendered, yay: showModal, nay: hideModal } = useTip(false);
  const { val: isLoading, yay: showLoader, nay: hideLoader } = useTip(false);

  const linkMarketingNotification = linkTo({ pattern: LINK.reminders, params: { salonId }, fault: '#' });

  // Activation flow: show subscription success text if billing was previously incomplete
  const isActivationFlow = fromAnyToBool(isNew || isBillingIncomplete);

  // NOTE:  Forces parent to fetch customer data after Stripe flow completes

  const handleSubscriptionConfirmation = useCallback(() => {
    hideModal();
    call('ClientCardForm.onChange', onChange);
  }, [hideModal, onChange]);

  return (
    <div
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div className={CN.section}>
        <div className={CN.inner}>
          <div className={CN.subTitle}>{t('staffAndBusiness.subAndBilling.billingDetails.section2Title')}</div>
          <div className={CN.text}>
            <p>{t('staffAndBusiness.subAndBilling.billingDetails.section2Description1')}</p>
            <div>
              {t('staffAndBusiness.subAndBilling.billingDetails.section2Description2')}
              <ExternalLink link={LINK_TERMS} variant="inline">
                <div className={CN.link}>{LINK_TERMS}</div>
              </ExternalLink>
            </div>
            <p>{t('staffAndBusiness.subAndBilling.billingDetails.section2Description3')}</p>
          </div>
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <SetupForm
              handleGoBack={handleGoBack}
              hideLoader={hideLoader}
              isNew={isNew}
              salonId={salonId}
              setShowModal={showModal}
              showLoader={showLoader}
              subscriptionId={subscriptionId}
            />
          </Elements>
        </div>
      </div>

      <SalonModal tag="ClientCardForm__modal-after-subscription" isOpen={isModalRendered} withFooter>
        <SalonModal.Header>
          <div className={SM.closeButtonContainer}>
            <Button
              tag="ClientCardForm__modal-close-and-reload"
              variant="empty"
              onClick={handleSubscriptionConfirmation}
            >
              <Icon variant="close" variantInvert />
            </Button>
          </div>
        </SalonModal.Header>
        <SalonModal.Body>
          <IconStatus width="lg" variant="success">
            <Icon selected width="xl" variant="confirm" />
          </IconStatus>
          {isActivationFlow ? (
            <div className={SM.modalContent}>
              <div className={SM.modalTitle}>You have activated your subscription!</div>
              <div className={SM.modalText}>
                <Space bold>
                  Subscription plan: Essential {signUpMethod === SIGN_UP_METHOD.self && '+ Digital Appointment Cards'}
                </Space>
              </div>
              <div className={SM.modalText}>
                {S.party} This includes unlimited free SMS reminders
                <Space>{signUpMethod === SIGN_UP_METHOD.self && '& confirmations'}</Space>
              </div>
              <div className={CN.alertOuter}>
                <AlertPanel variant="alert">
                  Please note you will be billed on 1st of every month based on your active columns and plan choice
                </AlertPanel>
              </div>
            </div>
          ) : (
            <div data-bem="ClientCardForm__modal-existing-card-data" className={SM.modalContent}>
              <div className={SM.modalTitle}>Card Details Updated.</div>
            </div>
          )}
        </SalonModal.Body>
        <SalonModal.Footer>
          {isActivationFlow ? (
            <div data-bem="ClientCardForm__modal-footer-button" className={CN.modalButtons}>
              <NavLink to={linkMarketingNotification}>
                <span className={CN.navLink}>MANAGE SMS NOTIFICATIONS</span>
              </NavLink>
            </div>
          ) : (
            <Button tag="ClientCardForm__ok" onClick={handleSubscriptionConfirmation} variant="primaryForce" xSpace>
              OK
            </Button>
          )}
        </SalonModal.Footer>
      </SalonModal>

      {isLoading && <OverlaySpinner />}
    </div>
  );
};

const ClientCardFormHoc = connect((state) => ({
  signUpMethod: selectCurrentSalonSignUpMethod(state),
}))(ClientCardForm);

export default ClientCardFormHoc;
